'use client';
import React from 'react';

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Try the engine',
    credits: '10 generations/month',
    features: [
      '10 content generations',
      'Hook + Script + Caption',
      'Viral score meter',
      'Basic trend signals',
    ],
    cta: 'Start Free',
    highlight: false,
  },
  {
    name: 'Creator',
    price: '$19',
    period: '/month',
    description: 'For serious creators',
    credits: '500 credits/month',
    features: [
      '500 content generations',
      'AI video generation',
      'Live trend detection',
      'Auto-post scheduling',
      'Advanced viral scoring',
      'Priority queue',
    ],
    cta: 'Get Creator',
    highlight: true,
  },
  {
    name: 'Agency',
    price: '$49',
    period: '/month',
    description: 'Full automation stack',
    credits: 'Unlimited automation',
    features: [
      'Unlimited generations',
      'Full automation pipeline',
      'Multi-account scheduling',
      'Bulk video processing',
      'Agency dashboard',
      'API access',
      'White-label exports',
    ],
    cta: 'Get Agency',
    highlight: false,
  },
];

export default function PricingSection() {
  return (
    <section id="pricing" className="py-24 px-6" style={{ background: 'var(--blackout)' }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold font-display mb-3" style={{ color: 'var(--chrome)' }}>
            Simple, transparent pricing
          </h2>
          <p className="text-base" style={{ color: 'var(--chrome-dim)' }}>
            Start free. Scale when you're ready.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS?.map((plan) => (
            <div
              key={plan?.name}
              className="relative p-6 rounded-2xl flex flex-col transition-all"
              style={{
                background: plan?.highlight ? 'rgba(194,24,91,0.08)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${plan?.highlight ? 'rgba(194,24,91,0.4)' : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              {plan?.highlight && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold"
                  style={{ background: 'var(--ruby)', color: 'white' }}
                >
                  Most Popular
                </div>
              )}

              <div className="mb-5">
                <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--chrome)' }}>
                  {plan?.name}
                </h3>
                <p className="text-xs mb-3" style={{ color: 'var(--chrome-dim)' }}>
                  {plan?.description}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold font-mono" style={{ color: plan?.highlight ? 'var(--ruby)' : 'var(--chrome)' }}>
                    {plan?.price}
                  </span>
                  <span className="text-sm" style={{ color: 'var(--chrome-dim)' }}>{plan?.period}</span>
                </div>
                <p className="text-xs mt-1 font-semibold" style={{ color: 'var(--ruby)' }}>
                  {plan?.credits}
                </p>
              </div>

              <ul className="space-y-2.5 mb-6 flex-1">
                {plan?.features?.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm" style={{ color: 'var(--chrome-dim)' }}>
                    <span style={{ color: 'var(--ruby)', flexShrink: 0 }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className="w-full py-3 rounded-xl text-sm font-semibold transition-all"
                style={
                  plan?.highlight
                    ? {
                        background: 'linear-gradient(135deg, var(--ruby) 0%, var(--ruby-dark) 100%)',
                        color: 'white',
                        border: '1px solid rgba(194,24,91,0.4)',
                      }
                    : {
                        background: 'rgba(255,255,255,0.05)',
                        color: 'var(--chrome)',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }
                }
                onMouseEnter={(e) => {
                  if (!plan?.highlight) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!plan?.highlight) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  }
                }}
              >
                {plan?.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold font-display mb-4" style={{ color: 'var(--chrome)' }}>
            Start creating viral content in seconds
          </h2>
          <p className="text-base mb-6" style={{ color: 'var(--chrome-dim)' }}>
            No credit card required. 10 free generations every month.
          </p>
          <a
            href="#generate"
            className="btn-ruby inline-block px-10 py-4 rounded-xl text-base font-semibold animate-ruby-pulse"
          >
            <span>⚡ Start Free — No Card Required</span>
          </a>
        </div>
      </div>
    </section>
  );
}
